import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type {
  DemandBgOption,
  DemandRetailerOption,
} from '@ebalance/proto/bffuser/v1/balance_model_pb'
import type { TFunction } from 'i18next'

const NO_LICENSE_TYPE_LABEL = '—'

/**
 * 「帰属不明」を表す予約 token。backend の `AggregatedSlotOwnerUnknown` (`-UNK`) と
 * 同じ文字列で、bg_code (BG Select) と retailer_code (小売 Select、1 件エリアの
 * 需要BG のみ) の両方が共有する。名前が BG 専用なのは先に BG で導入したため。
 */
export const UNATTRIBUTED_BG_CODE = '-UNK'

// `~/models/licenseType.ts` は OCCTO マスタ共通の短縮表記 (小売 / 自己託送) を
// 返すため、BG フィルターの語彙 (需要BG / 自己託送BG、doji-bg.md フィルター表) は
// 別関数として持つ (流用すると文言が spec と食い違う)。
export function bgOptionLicenseTypeLabel(
  licenseType: LicenseType,
  t: TFunction,
): string {
  switch (licenseType) {
    case LicenseType.RETAIL:
      return t('page:balanceDemandDetail.filter.bgLicenseTypeRetail')
    case LicenseType.SELF_WHEELING:
      return t('page:balanceDemandDetail.filter.bgLicenseTypeSelfWheeling')
    default:
      return NO_LICENSE_TYPE_LABEL
  }
}

export type BgOptionGroup = {
  licenseType: LicenseType
  options: readonly DemandBgOption[]
}

const BG_GROUP_ORDER: readonly LicenseType[] = [
  LicenseType.RETAIL,
  LicenseType.SELF_WHEELING,
]

/**
 * BG Select の選択肢を種別の見出しごとに分ける。BG 名自体が「〜需要BG」で
 * 終わることが多く、種別を名称の後ろの括弧で添えると重なるため (#9882 Des 判断)。
 * 需要BG → 自己託送BG の順で、BG の無い種別は見出しごと出さない。
 */
export function groupBgOptionsByLicenseType(
  options: readonly DemandBgOption[],
): BgOptionGroup[] {
  const groups = new Map<LicenseType, DemandBgOption[]>(
    BG_GROUP_ORDER.map((licenseType) => [licenseType, []]),
  )
  for (const option of options) {
    const group = groups.get(option.licenseType)
    if (group === undefined) {
      groups.set(option.licenseType, [option])
    } else {
      group.push(option)
    }
  }
  return [...groups]
    .filter(([, members]) => members.length > 0)
    .map(([licenseType, members]) => ({ licenseType, options: members }))
}

/**
 * (表示日, エリア) ごとの BG Select の選択肢。`unattributedSelectable` は「帰属不明」
 * を並べてよいかで、提出済み需要BGの件数から backend が決める (bg_options には
 * 載らない)。両方とも bg_code に依存しないので一緒に保持する。
 */
export type BgChoices = {
  bgOptions: readonly DemandBgOption[]
  unattributedSelectable: boolean
}

/**
 * 選択中の bgCode が選択肢に含まれる時はそのまま返し、含まれなければ「すべて」
 * (空文字) を返す。「帰属不明」は `unattributedSelectable` の時だけ含まれる。
 * 呼び出し側 (page) は選択肢を (表示日, エリア) 単位で保持してから本関数に渡す
 * (bgCode の変更だけでは選択肢は変わらないため)。
 */
export function resolveEffectiveBgCode(
  bgCode: string,
  choices: BgChoices,
): string {
  if (bgCode === UNATTRIBUTED_BG_CODE) {
    return choices.unattributedSelectable ? bgCode : ''
  }
  return choices.bgOptions.some((option) => option.bgCode === bgCode)
    ? bgCode
    : ''
}

/**
 * 効いている選択が個別 BG か。小売 Select を操作できる根拠で、「すべて」と
 * 「帰属不明」は個別 BG ではない。
 */
export function isIndividualBgCode(effectiveBgCode: string): boolean {
  return effectiveBgCode !== '' && effectiveBgCode !== UNATTRIBUTED_BG_CODE
}

/**
 * 「調達量・販売量」を出せる選択か (小売が「BG 全体」で、BG が「帰属不明」でない)。
 * 調達量・販売量は BG 全体の値にしか意味が無い (構成小売の調達には代表事業者に
 * 計上される JEPX 分が含まれない。doji-bg.md AC-BDB-031)。
 *
 * 選択中の値ではなく効いている値で判定する。選択肢外の小売は「BG 全体」として
 * 描くので、選択中の値で判定すると「BG 全体」の表示のまま系列が消える。
 */
export function isProcurementNetSelectable(
  effectiveBgCode: string,
  effectiveRetailerCode: string,
): boolean {
  return (
    effectiveBgCode !== UNATTRIBUTED_BG_CODE && effectiveRetailerCode === ''
  )
}

/**
 * 「需要計画値」を出せる選択か (BG・小売のどちらも「帰属不明」でない)。帰属不明は
 * 需要想定の確定までに小売へ振り分けるので計画値が無い (doji-bg.md AC-BDB-025-3)。
 */
export function isDemandPlanSelectable(
  effectiveBgCode: string,
  effectiveRetailerCode: string,
): boolean {
  return (
    effectiveBgCode !== UNATTRIBUTED_BG_CODE &&
    effectiveRetailerCode !== UNATTRIBUTED_BG_CODE
  )
}

/**
 * 実際に効いている BG の構成小売の選択肢。個別 BG に解決されていない時 (「すべて」・「帰属不明」・
 * 選択肢から消えた BG) は空。空であることは小売 Select の無効化の根拠ではない
 * (構成小売 0 名の個別 BG も空で、その時 Select は有効)。無効化は効いている BG が
 * 個別 BG かどうかで決める。
 */
export function retailersOfEffectiveBg(
  effectiveBgCode: string,
  bgOptions: readonly DemandBgOption[],
): readonly DemandRetailerOption[] {
  if (!isIndividualBgCode(effectiveBgCode)) return []
  return (
    bgOptions.find((option) => option.bgCode === effectiveBgCode)?.retailers ??
    []
  )
}

/**
 * 実際に効いている BG が自身の実績に帰属不明を含み、小売 Select に「帰属不明」を
 * 並べてよいか。個別 BG に解決されていない時は false。
 */
export function includesUnattributedOfEffectiveBg(
  effectiveBgCode: string,
  bgOptions: readonly DemandBgOption[],
): boolean {
  if (!isIndividualBgCode(effectiveBgCode)) return false
  return (
    bgOptions.find((option) => option.bgCode === effectiveBgCode)
      ?.includesUnattributed ?? false
  )
}

/**
 * 選択中の retailerCode が選択肢に含まれる時だけそのまま返し、含まれなければ
 * 「BG 全体」(空文字) を返す。4 桁は効いている BG の構成小売への包含、`-UNK` は
 * 効いている BG の `includesUnattributed` で判定する (BG の `-UNK` +
 * `unattributedSelectable` と同じ pattern)。BG が「すべて」の時は常に空文字
 * (小売は個別 BG の時だけ効く)。
 */
export function resolveEffectiveRetailerCode(
  retailerCode: string,
  effectiveBgCode: string,
  bgOptions: readonly DemandBgOption[],
): string {
  if (retailerCode === UNATTRIBUTED_BG_CODE) {
    return includesUnattributedOfEffectiveBg(effectiveBgCode, bgOptions)
      ? retailerCode
      : ''
  }
  return retailersOfEffectiveBg(effectiveBgCode, bgOptions).some(
    (retailer) => retailer.businessCode === retailerCode,
  )
    ? retailerCode
    : ''
}
